import { useEffect, useState } from 'react';
import { api } from '@/shared/api/client';
import type { Print, Product } from '@/shared/api/schemas';
import { demoCatalog, demoPrints } from '@/features/partner/demo-catalog';
import { DEMO_PRINTS, DEMO_PRODUCTS } from './demo';

interface CatalogState {
  products: Product[];
  prints: Print[];
  isLoading: boolean;
  /** Данные подставлены локально: API не ответил. */
  isDemo: boolean;
  error: string | null;
}

/**
 * Каталог партнёра, загруженный один раз на сеанс.
 *
 * Товары и принты меняются раз в неделю, а виджет спрашивал их при каждом
 * появлении компонента: возврат на витрину с четвёртого шага заново
 * дёргал сеть, и на секунду показывалось «загружаем» поверх уже виденного
 * каталога. Держим ответ в памяти модуля и отдаём его сразу.
 *
 * Кэш живёт в памяти вкладки, а не в хранилище браузера: цена и наличие
 * должны приезжать свежими при следующем открытии.
 */
const cache = new Map<string, CatalogState>();

/** Незавершённые запросы, чтобы два компонента не спрашивали одно дважды. */
const inFlight = new Map<string, Promise<CatalogState>>();

/** Чем подменить каталог, когда сервера нет: своим для заказчика или общим. */
function stub(slug: string): Product[] {
  return demoCatalog(slug) ?? DEMO_PRODUCTS;
}

/** То же для принтов: у заказчика своя библиотека, а не принты артистов. */
function stubPrints(slug: string): Print[] {
  return demoPrints(slug) ?? DEMO_PRINTS;
}

async function load(slug: string): Promise<CatalogState> {
  try {
    const [products, prints] = await Promise.all([api.products(slug), api.prints(slug)]);

    // Партнёр без товаров — тоже повод показать демо, иначе экран пустой.
    const isEmpty = products.length === 0;
    return {
      products: isEmpty ? stub(slug) : products,
      prints: prints.length === 0 ? stubPrints(slug) : prints,
      isLoading: false,
      isDemo: isEmpty,
      error: null,
    };
  } catch (cause: unknown) {
    const detail = cause instanceof Error ? cause.message : 'нет связи с сервером';
    return {
      products: stub(slug),
      prints: stubPrints(slug),
      isLoading: false,
      isDemo: true,
      error: detail,
    };
  }
}

export function useCatalog(slug: string): CatalogState {
  const [state, setState] = useState<CatalogState>(
    () =>
      cache.get(slug) ?? {
        products: [],
        prints: [],
        isLoading: true,
        isDemo: false,
        error: null,
      },
  );

  useEffect(() => {
    const cached = cache.get(slug);
    if (cached) {
      setState(cached);
      return;
    }

    let isCancelled = false;

    // Один запрос на слаг: второй компонент подхватывает тот же промис.
    const request = inFlight.get(slug) ?? load(slug);
    inFlight.set(slug, request);

    void request.then((result) => {
      cache.set(slug, result);
      inFlight.delete(slug);
      if (!isCancelled) setState(result);
    });

    return () => {
      isCancelled = true;
    };
  }, [slug]);

  return state;
}
